import { makeAutoObservable } from 'mobx';
import toast from 'react-hot-toast';
import { isValidEmail, isValidUsername, safeRedirectPath } from 'hevy-shared';
import { stores } from 'state/stores';
import { fetchUserContentAfterLogin } from 'utils/fetchUserContent';
import API, { SocialLoginResponse } from 'utils/API';
import { log } from 'utils/log';
import { sendEvent } from 'utils/events';
import { modal } from 'components/Modals/ModalManager';
import axios from 'axios';
import { CodeResponse } from '@react-oauth/google';
import router from 'next/router';
import { themeState } from 'styleguide/theme';
import { t } from 'translations/t';
import { recaptcha } from 'screens/Signup/utils';

type ScreenState = 'login' | 'forgot-password';

interface LoginViewModelProps {
  afterLogin?: () => Promise<void>;
}

export class LoginViewModel {
  usernameOrEmail: string = '';
  password: string = '';
  errorMessage?: string;
  gympassUserId?: string;
  screenState: ScreenState = 'login';
  isLoading: boolean = false;
  afterLogin?: () => Promise<void>;

  constructor(props?: LoginViewModelProps) {
    this.afterLogin = props?.afterLogin;
    makeAutoObservable(this);

    log('LoginViewModel constructor');
    sendEvent('login_screenview');
  }

  get isValidEmailorUsername() {
    return isValidEmail(this.usernameOrEmail) || isValidUsername(this.usernameOrEmail);
  }

  get isValidPassword() {
    return this.password.length > 5;
  }

  get isLoginButtonEnabled() {
    return this.usernameOrEmail.length > 0 && this.password.length > 0;
  }

  setScreenState = (screenState: ScreenState) => {
    this.screenState = screenState;
  };

  redirectLoggedInUser = (isNewUser: boolean) => {
    if (isNewUser) {
      router.push('/onboarding/username');
      return;
    }
    router.push(safeRedirectPath(router.query.postLoginPath, '/'));
  };

  onLogin = async () => {
    if (!this.isLoginButtonEnabled) return;

    themeState.push('light');
    try {
      await this.onLoginPress();
      const initialRoute = safeRedirectPath(router.query.postLoginPath, '/');

      const isOnboardingTestUser = stores.account.username === 'onboarding';
      router
        .push(isOnboardingTestUser ? '/onboarding/units' : initialRoute)
        .then(() => themeState.pop());
    } catch {
      themeState.pop();
    }
  };

  /**
   * Google Login
   */

  onLoginWithGoogleSuccess = async (response: CodeResponse) => {
    sendEvent('login_click', { source: 'google' });

    let loginResult: SocialLoginResponse;

    try {
      loginResult = await stores.auth.loginWithGoogle(response.code, this.gympassUserId);
    } catch {
      this.onLoginWithGoogleError();
      return;
    }

    sendEvent('login_complete', { source: 'google' });

    await this.afterLogin?.();

    await fetchUserContentAfterLogin({ isNewUser: loginResult.is_new_user || undefined });
    this.redirectLoggedInUser(loginResult.is_new_user);
  };

  onLoginWithGoogleError = () => {
    this.errorMessage = t('web.login.error.google');
  };

  /**
   * Apple Login
   */

  onLoginWithAppleClick = async () => {
    sendEvent('login_click', { source: 'apple' });
    this.errorMessage = undefined;

    let loginResult: SocialLoginResponse | null = null;

    try {
      const result = await (window as any).AppleID?.auth?.signIn();
      const identityToken = result.authorization?.id_token;
      const maybeEmail = result.user?.email;
      loginResult = await stores.auth.loginWithApple({
        identityToken,
        email: maybeEmail,
        gympassUserId: this.gympassUserId,
      });
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.data.error === 'popup_closed_by_user') return;

      this.errorMessage = t('web.login.error.apple');
      return;
    }

    sendEvent('login_complete', { source: 'apple' });

    await this.afterLogin?.();

    await fetchUserContentAfterLogin({ isNewUser: loginResult.is_new_user || undefined });

    if (!loginResult) {
      console.log('no result');
      return;
    }
    this.redirectLoggedInUser(loginResult.is_new_user);
  };

  /**
   * Email Login
   */

  onUpdateUsernameOrEmail = (value: string) => {
    this.usernameOrEmail = value;
  };

  onUpdatePassword = (value: string) => {
    this.password = value;
  };

  onLoginPress = async () => {
    sendEvent('login_click', { source: 'email' });
    this.errorMessage = undefined;

    try {
      this.isLoading = true;
      const token = await recaptcha('login');
      await stores.auth.login(this.usernameOrEmail, this.password, this.gympassUserId, token);
      sendEvent('login_complete', { source: 'email' });
      await this.afterLogin?.();
      await fetchUserContentAfterLogin();
    } catch (e: unknown | import('axios').AxiosError) {
      this.isLoading = false;

      if (axios.isAxiosError(e)) {
        if (e.response?.status === 429) {
          this.errorMessage = t('web.coach.clientOnboarding.verificationResend.tooManyRequests');
        } else {
          switch (e.response?.data.error) {
            case 'InvalidEmailOrPassword':
              this.errorMessage = t('web.login.error.emailPassword');
              break;
            default:
              this.errorMessage = t('web.login.error.unknown');
              sendEvent('login_error', { source: 'email', error: e.message });
          }
        }

        throw e;
      }

      this.errorMessage = t('global.unknownError');
      throw e;
    }
  };

  passwordRecoveryEmail: string = '';
  isPasswordRecoveryEmailLoading: boolean = false;
  onUpdatePasswordRecoveryEmail = (value: string) => {
    this.passwordRecoveryEmail = value;
  };

  get isSendPasswordRecoveryEnabled() {
    return isValidEmail(this.passwordRecoveryEmail);
  }

  onSendPasswordRecovery = async () => {
    this.isPasswordRecoveryEmailLoading = true;
    try {
      await toast.promise(API.generatePasswordRecoveryEmail(this.passwordRecoveryEmail), {
        loading: t('web.login.passwordRecovery.loading'),
        success: t('web.login.passwordRecovery.success'),
        error: t('web.login.passwordRecovery.error'),
      });
      this.setScreenState('login');
      modal.ok({
        title: t('web.login.passwordRecovery.modal.title'),
        subtitle: t('web.login.passwordRecovery.modal.subtitle'),
        submitButtonTitle: t('web.login.passwordRecovery.modal.submitBtn'),
      });
    } catch {
    } finally {
      this.isPasswordRecoveryEmailLoading = false;
    }
  };
}
